import { CheckCircle2, AlertCircle, Users, Scale, Gauge } from 'lucide-react';
import type { RankedProvider, Prioritization } from '@vagusx/clients';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { describeContribution } from '@/components/custom/appointment-type-recommendation';

/**
 * Shared, presentation-only building blocks for the ranked provider pool.
 *
 * Same role as `appointment-type-recommendation.tsx` and deliberately the same
 * shape: the settings simulator, the Tasks Overview referral card, and the call
 * drawer must all explain a ranking the SAME way, or staff comparing two
 * screens will read them as two different algorithms.
 *
 * A provider tied across two enabled types renders read-only (both names); the
 * type is chosen when the appointment is actually booked, not from here.
 */

// =============================================================================
// Tier
// =============================================================================

/**
 * Tier tone, best → weakest. Built from the theme's `--success` / `--warning`
 * tokens rather than a fixed palette, because those are already tuned per mode
 * (see the note beside them in `index.css`).
 *
 * The colour lives in the BACKGROUND and border; the label stays on
 * `foreground`. A tinted surface reads correctly under both themes, whereas
 * coloured text has to clear contrast against a white card in light mode and a
 * near-black one in dark — and `--warning` is too light to do that on white.
 */
const TIER_STYLES = [
  'border-success/40 bg-success/15 text-foreground',
  'border-border bg-muted text-muted-foreground',
  'border-warning/40 bg-warning/15 text-foreground',
] as const;

/** Rank 1 gets a tinted card. `/10` so it reads as emphasis, not as a state. */
const TOP_RANK_SURFACE = 'border-success/40 bg-success/10';

/**
 * The provider a host has picked (the campaign queue's slots panel). A ring
 * drawn over the row's own surface rather than a surface of its own, so the
 * rank-1 tint survives being picked and a row reads the same here as in the
 * read-only popover: colour says how good a match, the ring says which is open.
 */
const SELECTED_RING = 'ring-2 ring-primary';

/**
 * Plain-language label for a tier. Tier is the coarse MATCH bucket, and it is
 * the thing staff most need to understand: priority never reorders across it,
 * so a lower-tier provider cannot outrank a higher-tier one no matter how
 * lightly booked or highly rated they are.
 */
export function describeTier(provider: RankedProvider): string {
  if (provider.isDefaultFallback) return 'Default fallback';
  if (provider.codeMatchCount === 0) return 'No code match';
  return `${provider.codeMatchCount} code${provider.codeMatchCount === 1 ? '' : 's'} matched`;
}

export function TierBadge({ provider }: { provider: RankedProvider }): React.ReactElement {
  const className = TIER_STYLES[Math.min(provider.tier - 1, TIER_STYLES.length - 1)];
  return (
    <Badge variant="outline" className={cn('text-xs font-medium', className)}>
      {describeTier(provider)}
    </Badge>
  );
}

// =============================================================================
// Formatting helpers
// =============================================================================

/** Scores are 0..1; three decimals is enough to see a tuning change move them. */
export function formatScore(value: number | null): string {
  return value === null ? '—' : value.toFixed(3);
}

/**
 * The composite spelled out as arithmetic — `0.6 × 0.333 + 0.4 × 0.900 = 0.560`.
 *
 * This is the whole point of the panel. Staff are here to decide whether the
 * algorithm is behaving, and a bare number gives them nothing to check; the
 * expansion lets them see which strategy actually moved the ordering.
 */
export function buildCompositeExpression(
  provider: RankedProvider,
  prioritization: Prioritization
): string | null {
  if (provider.compositeScore === null) return null;

  const terms: string[] = [];
  for (const { key, weight } of prioritization.strategies) {
    const score = key === 'fairness' ? provider.fairnessScore : provider.weightScore;
    if (score === null) continue;
    terms.push(`${weight} × ${score.toFixed(3)}`);
  }
  if (terms.length === 0) return null;

  return `${terms.join(' + ')} = ${provider.compositeScore.toFixed(3)}`;
}

// =============================================================================
// Per-provider appointment type
// =============================================================================

/** The provider's location suffix, e.g. ` · Apple Valley`. */
function locationSuffix(provider: RankedProvider): string {
  return provider.locationName ? ` · ${provider.locationName}` : '';
}

/**
 * The type line under a provider's name. Resolved → the type name. Tied across
 * two enabled types → both names, read-only (the type is chosen at booking time,
 * not from this explanation view).
 */
function ProviderTypeLine({ provider }: { provider: RankedProvider }): React.ReactElement {
  if (provider.isTie) {
    return (
      <p className="truncate text-xs text-warning">
        {provider.tiedTypes.map((t) => t.appointmentTypeName).join(' / ')} — tie
        {locationSuffix(provider)}
      </p>
    );
  }

  return (
    <p className="truncate text-xs text-muted-foreground">
      {provider.appointmentTypeName}
      {locationSuffix(provider)}
    </p>
  );
}

// =============================================================================
// Compact list
// =============================================================================

interface RankedProviderListProps {
  providers: RankedProvider[];
  /** Highlights the row for the provider already chosen, when there is one. */
  selectedProviderId?: string | undefined;
  /**
   * With `selectedProviderId`, the location of the row chosen: the pool's rows
   * are a provider at a location, so a provider ranked at two is two rows.
   */
  selectedLocationId?: string | undefined;
  /** When set, rows become clickable — used by the call drawer to set the picker. */
  onSelect?: ((provider: RankedProvider) => void) | undefined;
}

export function RankedProviderList({
  providers,
  selectedProviderId,
  selectedLocationId,
  onSelect,
}: RankedProviderListProps): React.ReactElement {
  if (providers.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
        No provider is eligible for this referral, and no default provider is configured.
      </div>
    );
  }

  return (
    <ol className="space-y-1.5">
      {providers.map((provider) => {
        const isSelected =
          provider.providerId === selectedProviderId &&
          (selectedLocationId === undefined || provider.locationId === selectedLocationId);
        const interactive = !!onSelect;

        return (
          <li key={`${provider.providerId}:${provider.locationId}`}>
            <div
              role={interactive ? 'button' : undefined}
              tabIndex={interactive ? 0 : undefined}
              onClick={interactive ? () => onSelect?.(provider) : undefined}
              onKeyDown={
                interactive
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelect?.(provider);
                      }
                    }
                  : undefined
              }
              className={cn(
                'flex items-start gap-3 rounded-lg border p-2.5',
                provider.rank === 1 && TOP_RANK_SURFACE,
                isSelected && SELECTED_RING,
                interactive && 'cursor-pointer',
                interactive && !isSelected && 'hover:bg-muted/50'
              )}
            >
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                {provider.rank}
              </span>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate text-sm font-medium">{provider.providerName}</span>
                  <TierBadge provider={provider} />
                </div>
                <ProviderTypeLine provider={provider} />
              </div>

              <span className="mt-0.5 shrink-0 text-xs tabular-nums text-muted-foreground">
                {formatScore(provider.compositeScore)}
              </span>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// =============================================================================
// Reasons detail
// =============================================================================

interface ProviderRankingReasonsProps {
  providers: RankedProvider[];
  prioritization: Prioritization;
}

/**
 * Per-provider breakdown: the match signals that set the tier, then each
 * strategy's normalized sub-score, then the composite arithmetic.
 */
export function ProviderRankingReasons({
  providers,
  prioritization,
}: ProviderRankingReasonsProps): React.ReactElement {
  if (providers.length === 0) {
    return <p className="text-xs text-muted-foreground">Nothing to explain — the pool is empty.</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
        <Users className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          Providers are grouped by how well they match the referral first. Availability and weight
          only reorder providers <strong>within</strong> the same match group — they never move a
          weaker clinical match ahead of a stronger one.
        </span>
      </div>

      {providers.map((provider) => {
        const positives = provider.contributions.filter((c) => c.points > 0);
        const negatives = provider.contributions.filter((c) => c.points < 0);
        const expression = buildCompositeExpression(provider, prioritization);

        return (
          <div
            key={`${provider.providerId}:${provider.locationId}`}
            className={cn(
              'space-y-1.5 rounded-lg border p-3',
              provider.rank === 1 && TOP_RANK_SURFACE
            )}
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium">
                {provider.rank}. {provider.providerName}
              </span>
              <TierBadge provider={provider} />
            </div>

            {provider.isDefaultFallback && (
              <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                <AlertCircle className="mt-0.5 h-3 w-3 shrink-0 text-warning" />
                No provider matched this referral — this is the configured fallback, not a match.
              </p>
            )}

            {positives.map((c, i) => (
              <p key={`p${i}`} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-success" />
                {describeContribution(c)}
              </p>
            ))}
            {negatives.map((c, i) => (
              <p key={`n${i}`} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                <AlertCircle className="mt-0.5 h-3 w-3 shrink-0 text-warning" />
                {describeContribution(c)}
              </p>
            ))}

            {(provider.fairnessScore !== null || provider.weightScore !== null) && (
              <div className="flex flex-wrap gap-3 pt-0.5">
                {provider.fairnessScore !== null && (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Scale className="h-3 w-3 shrink-0" />
                    Availability {formatScore(provider.fairnessScore)}
                  </span>
                )}
                {provider.weightScore !== null && (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Gauge className="h-3 w-3 shrink-0" />
                    Weight {formatScore(provider.weightScore)}
                  </span>
                )}
              </div>
            )}

            {expression && (
              <p className="text-xs tabular-nums text-muted-foreground">Priority: {expression}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}
